import api from '@/rest-api';

type EventType = 'JobStateChanged'
type Handler = (data: any) => boolean | void; // a truthy return value will deregister the handler
type TypedHandler = { type: EventType; handler: Handler }
type ErrorHandler = (ev: Event, sse: SSE) => void

export interface HandlerRegistration {
  unsubscribe();
}

interface SSE {
  onError(handler: ErrorHandler): SSE;
  register(type: EventType, handler: Handler): SSE;
  deregister(type: EventType, handler: Handler): SSE;
  state(): 'closed' | 'connecting' | 'open' | undefined;
  close(): void;
}

const sse = {
  url: api.sseSubscriptionUrl(),
  connection: undefined as SSE | undefined,
  isConnected(): boolean {
    return this.connection?.state() === 'open';
  },
  withConnection(action: (con: SSE) => void) {
    if (this.connection && this.isConnected()) {
      action(this.connection);
    } else {
      connect(this.url, false)
        .then((con) => {
          con.onError((ev) => {
            const reconnecting = con.state() === 'connecting';
            // eslint-disable-next-line no-console
            console.error(`sse error${reconnecting ? ' (reconnecting) ' : ''}: ${ev}`, ev);
          });
          this.connection = con;
          action(con);
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error(`Could not connect to SSE endpoint: ${this.url}: ${e}`);
        });
    }
  },
  subscribe({ type, handler }: TypedHandler): HandlerRegistration {
    this.withConnection((con: SSE) => {
      con.register(type, handler);
    });
    return {
      unsubscribe: () => this.unsubscribe({ type, handler }),
    };
  },
  unsubscribe({ type, handler }: TypedHandler) {
    if (this.connection) {
      this.connection.deregister(type, handler);
    }
  },
};

export default sse;

function connect(url: string, withCredentials: boolean): Promise<SSE> {
  const source = new EventSource(url, { withCredentials });
  return new Promise((resolve, reject) => {
    source.onerror = reject;
    source.onopen = () => {
      source.onerror = null;
      resolve(new SSEImpl(source));
    };
  });
}

class SSEImpl implements SSE {
  private source: EventSource;

  private handlersByType: { [key in EventType]?: Array<{ handler: Handler; listener?: EventListener }>};

  constructor(source: EventSource) {
    this.source = source;
    this.handlersByType = {};
  }

  public state() {
    switch (this.source.readyState) {
      case EventSource.CLOSED: return 'closed';
      case EventSource.CONNECTING: return 'connecting';
      case EventSource.OPEN: return 'open';
      default: return undefined;
    }
  }

  public onError(handler: ErrorHandler) {
    const h: (s: EventSource, e: Event) => any = (s: EventSource, e: Event) => {
      handler(e, this);
    };
    this.source.onerror = h as any;
    return this;
  }

  public register(type: EventType, handler: Handler) {
    const listener: EventListener = (event) => {
      let data;
      try {
        data = JSON.parse((event as any).data);
      } catch (err) {
        if (typeof this.source.onerror === 'function') {
          this.source.onerror(err as any);
        }
      }
      const deregister = handler(data);
      if (deregister) {
        this.deregister(type, handler);
      }
    };
    this.addHandler(type, handler, listener);
    return this;
  }

  public deregister(type: EventType, handler: Handler) {
    this.removeHandler(type, handler);
    if (!this.hasHandlers()) {
      this.close();
    }
    return this;
  }

  public close() {
    this.source.close();
    Object.keys(this.handlersByType).forEach((type) => {
      this.handlersByType[type] = undefined;
    });
  }

  private hasHandlers(): boolean {
    return Object.values(this.handlersByType).some((hs) => !!hs && hs.length > 0);
  }

  private addHandler(type: EventType, handler: Handler, listener: EventListener) {
    if (!this.handlersByType[type]) {
      this.handlersByType[type] = [];
    }
    (this.handlersByType[type] as Array<{handler: Handler; listener?: EventListener}>).push({ handler, listener });
    this.source.addEventListener(type, listener);
  }

  private removeHandler(type: EventType, handler: Handler): boolean {
    const stack = this.handlersByType[type];
    if (!stack) {
      return false;
    }
    for (let i = 0; i < stack.length; i++) {
      if (stack[i].handler === handler) {
        const { listener } = stack[i];
        stack.splice(i, 1);
        if (listener) {
          this.source.removeEventListener(type, listener);
        }
        return true;
      }
    }
    return false;
  }
}
